import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Clock, Users, Heart, Sparkles, Sun, Moon, Music } from "lucide-react";
import Navigation from "../components/shared/Navigation";
import Footer from "../components/shared/Footer";
import PageBreadcrumb from "../components/shared/PageBreadcrumb";
import { usePageMeta } from "../components/shared/SEOHelper";

const WhatToExpectYogaRetreat = () => {
  usePageMeta({
    title: "What to Expect at a Yoga Retreat | First Time Yoga Retreat",
    description: "Wondering what to expect at your first yoga retreat? Complete guide covering daily schedules, yoga styles, food, accommodation, what to bring, and how to",
    canonical: "https://annelielily.com/retreats/what-to-expect"
  });

  return (
    <div className="min-h-screen bg-cream">
      <Navigation />
      <PageBreadcrumb currentPage="What to Expect" currentSlug="retreats/what-to-expect" />
      
      {/* Hero */}
      <section className="pt-28 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/retreats/yoga-retreats-uk" className="text-gold text-sm hover:underline mb-4 inline-block">
              ← Back to Yoga Retreats UK
            </Link>
            <h1 className="font-heading text-4xl md:text-5xl font-light text-neutral-800 mb-5">
              What to Expect at a Yoga Retreat
            </h1>
            <p className="text-neutral-700 max-w-2xl mx-auto leading-relaxed">
              Thinking about your first yoga retreat but not sure what it involves? 
              This complete guide covers everything you need to know — from daily schedules 
              to what you'll eat, wear, and experience.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="prose-content space-y-8"
          >
            {/* Intro */}
            <div>
              <h2 className="font-heading text-2xl text-neutral-800 mb-4">
                Your First Yoga Retreat: What to Really Expect
              </h2>
              <p className="text-neutral-700 leading-relaxed">
                If you've never been on a yoga retreat before, it's natural to have questions. 
                Will everyone else be super flexible? Is it all silence and meditation? What if 
                I can't do the poses? Let me put your mind at ease — <strong>yoga retreats are 
                far more welcoming and accessible than you might imagine</strong>.
              </p>
              <p className="text-neutral-700 leading-relaxed">
                A retreat is simply dedicated time away from everyday life to focus on your 
                wellbeing. It's not a bootcamp. It's not competitive. And it's definitely not 
                about being "good at yoga."
              </p>
            </div>

            {/* Daily Structure */}
            <div className="bg-muted p-6 md:p-8 rounded-2xl">
              <h2 className="font-heading text-2xl text-neutral-800 mb-4">
                A Typical Day at a Yoga Retreat
              </h2>
              <p className="text-neutral-700 mb-6">
                While every retreat is different, most follow a similar rhythm designed to help 
                you relax, restore, and reconnect. Here's what a typical day might look like:
              </p>
              
              <div className="space-y-4">
                {[
                  { time: "7:00-8:00am", title: "Gentle Wake Up", desc: "Optional early meditation or free time. No one forces you out of bed!" },
                  { time: "8:00-9:00am", title: "Breakfast", desc: "Nourishing vegetarian breakfast, often with fresh fruit, yogurt, and warm options" },
                  { time: "9:30-11:30am", title: "Morning Yoga", desc: "The main practice — typically more energising (Hatha, Vinyasa, or Kundalini)" },
                  { time: "12:00-1:00pm", title: "Lunch", desc: "The biggest meal of the day, followed by rest time" },
                  { time: "1:00-4:00pm", title: "Free Time", desc: "Rest, read, walk in nature, book treatments, or simply be" },
                  { time: "4:00-5:30pm", title: "Afternoon Session", desc: "Often gentler — Yin yoga, breathwork, meditation, or workshops" },
                  { time: "6:00-7:00pm", title: "Dinner", desc: "Light evening meal, often followed by free time or evening activities" },
                  { time: "8:00pm onwards", title: "Evening", desc: "Optional activities like sound baths, fire circles, or early rest" }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 items-start">
                    <div className="w-28 flex-shrink-0">
                      <span className="text-gold text-sm font-medium">{item.time}</span>
                    </div>
                    <div>
                      <h4 className="font-medium text-neutral-800">{item.title}</h4>
                      <p className="text-neutral-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* The Yoga */}
            <div>
              <h2 className="font-heading text-2xl text-neutral-800 mb-4">
                What the Yoga Sessions Are Like
              </h2>
              <p className="text-neutral-700 leading-relaxed mb-4">
                Yoga on retreat is <strong>accessible to all levels</strong>. Teachers offer 
                modifications for every pose, and there's zero pressure to do anything that 
                doesn't feel right for your body. If you need to rest, you rest.
              </p>
              <p className="text-neutral-700 leading-relaxed mb-4">
                You might experience several styles of yoga during a retreat:
              </p>
              <ul className="space-y-3">
                {[
                  { name: "Hatha Yoga", desc: "Balanced postures held for several breaths — great for beginners" },
                  { name: "Vinyasa/Flow", desc: "Linking movement with breath in flowing sequences" },
                  { name: "Kundalini", desc: "Dynamic practice combining movement, breathwork, and mantra" },
                  { name: "Yin Yoga", desc: "Slow, deep stretches held for 3-5 minutes — deeply relaxing" },
                  { name: "Restorative", desc: "Supported poses using props for complete relaxation" },
                  { name: "Yoga Nidra", desc: "Guided relaxation lying down — like a conscious sleep" }
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                    <span className="text-neutral-700">
                      <strong>{item.name}:</strong> {item.desc}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Beyond Yoga */}
            <div>
              <h2 className="font-heading text-2xl text-neutral-800 mb-4">
                What Else Happens Beyond Yoga
              </h2>
              <p className="text-neutral-700 leading-relaxed mb-4">
                Most retreats include more than just yoga classes. You might experience:
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: Music, title: "Sound Healing", desc: "Singing bowls, gongs, and crystal bowls for deep relaxation" },
                  { icon: Heart, title: "Meditation", desc: "Guided practices to calm the mind and cultivate presence" },
                  { icon: Sun, title: "Breathwork", desc: "Pranayama techniques for energy and clarity" },
                  { icon: Sparkles, title: "Ceremonies", desc: "Cacao ceremonies, fire circles, or opening/closing rituals" }
                ].map((item, i) => (
                  <div key={i} className="bg-white/70 p-4 rounded-xl border border-gold/10">
                    <item.icon className="w-5 h-5 text-gold mb-2" />
                    <h4 className="font-medium text-neutral-800 text-sm">{item.title}</h4>
                    <p className="text-neutral-600 text-xs">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Food */}
            <div className="bg-white/70 p-6 md:p-8 rounded-2xl border border-gold/10">
              <h2 className="font-heading text-2xl text-neutral-800 mb-4">
                What You'll Eat
              </h2>
              <p className="text-neutral-700 leading-relaxed mb-4">
                Retreat food is typically <strong>vegetarian and nourishing</strong> — designed to 
                support your practice without leaving you feeling heavy. Expect fresh, wholesome 
                meals with plenty of vegetables, grains, and plant-based proteins.
              </p>
              <p className="text-neutral-700 leading-relaxed mb-4">
                Most retreats happily cater to dietary requirements including vegan, gluten-free, 
                and allergies — just let them know when booking.
              </p>
              <p className="text-neutral-700 leading-relaxed">
                <strong>Alcohol is usually not served</strong> on yoga retreats, though policies 
                vary. The focus is on giving your body a break and allowing the practices to work 
                their magic without interference.
              </p>
            </div>

            {/* Accommodation */}
            <div>
              <h2 className="font-heading text-2xl text-neutral-800 mb-4">
                Where You'll Stay
              </h2>
              <p className="text-neutral-700 leading-relaxed mb-4">
                Accommodation varies depending on the retreat. Options typically include:
              </p>
              <ul className="space-y-2 mb-4">
                {[
                  "Shared rooms (usually twin) — often the most affordable option",
                  "Single rooms — available at most retreats with a supplement",
                  "En-suite or shared bathrooms — varies by venue",
                  "Glamping, yurts, or eco-cabins at some locations"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                    <span className="text-neutral-700">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-neutral-700 leading-relaxed">
                Day retreats don't require accommodation — you simply arrive in the morning and 
                leave in the late afternoon.
              </p>
            </div>

            {/* Who Goes */}
            <div>
              <h2 className="font-heading text-2xl text-neutral-800 mb-4">
                Who Goes on Yoga Retreats?
              </h2>
              <p className="text-neutral-700 leading-relaxed mb-4">
                You'll find a mix of people on retreat — <strong>mostly women, though men are always 
                welcome too</strong>. Ages typically range from late 20s to 60s+, with varying levels 
                of yoga experience from complete beginners to teachers.
              </p>
              <p className="text-neutral-700 leading-relaxed mb-4">
                Many people come alone, making retreats a wonderful way to meet like-minded people. 
                The shared experience creates natural connections without the pressure of forced 
                socialising.
              </p>
              <p className="text-neutral-700 leading-relaxed">
                What everyone has in common: a desire to step away from busy life, take care of 
                themselves, and try something nourishing.
              </p>
            </div>

            {/* Common Concerns */}
            <div className="bg-muted p-6 md:p-8 rounded-2xl">
              <h2 className="font-heading text-2xl text-neutral-800 mb-4">
                Common Concerns (And Why They're Not a Problem)
              </h2>
              <div className="space-y-4">
                {[
                  { q: "I'm not flexible at all", a: "Yoga isn't about flexibility — it's about awareness and breath. Every pose can be modified." },
                  { q: "I've never done yoga before", a: "Many people try yoga for the first time on retreat. Beginners are always welcome." },
                  { q: "I'm worried about being the oldest/youngest/biggest/etc.", a: "Yoga is for every body. You'll find a welcoming, non-judgmental space." },
                  { q: "I don't know anyone", a: "Most people come solo. The intimate setting makes it easy to connect." },
                  { q: "I'm not spiritual", a: "You can take what resonates and leave the rest. There's no belief system required." }
                ].map((item, i) => (
                  <div key={i}>
                    <p className="font-medium text-neutral-800 mb-1">"{item.q}"</p>
                    <p className="text-neutral-600 text-sm">{item.a}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* How to Prepare */}
            <div>
              <h2 className="font-heading text-2xl text-neutral-800 mb-4">
                How to Prepare for Your First Retreat
              </h2>
              <ol className="space-y-3 list-decimal list-inside text-neutral-700">
                <li><strong>Book well in advance</strong> — popular retreats fill up quickly</li>
                <li><strong>Read the pre-retreat information</strong> — you'll receive details about what to bring</li>
                <li><strong>Clear your schedule</strong> — avoid booking things for immediately before or after</li>
                <li><strong>Set an intention</strong> — what do you hope to experience or release?</li>
                <li><strong>Pack light</strong> — yoga clothes, layers, and minimal toiletries (see our <Link to="/retreats/packing-list" className="text-gold hover:underline">packing list</Link>)</li>
                <li><strong>Arrive with an open mind</strong> — the experience might surprise you</li>
              </ol>
            </div>

          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Ready to Experience a Retreat?
            </h2>
            <p className="text-neutral-600 mb-6">
              Explore our upcoming yoga retreats in the UK and abroad, suitable for all levels.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/retreats/yoga-retreats-uk" className="btn-primary">
                View Yoga Retreats UK <ArrowRight className="w-4 h-4 inline ml-2" />
              </Link>
              <Link to="/contact" className="btn-secondary">
                Ask a Question
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Links */}
      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h3 className="font-heading text-lg text-neutral-800 mb-4 text-center">Related Guides</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <Link to="/retreats/packing-list" className="bg-white/70 p-4 rounded-xl border border-gold/10 hover:border-gold/30 transition-all text-center">
              <span className="text-neutral-800 font-medium text-sm">Packing List</span>
              <p className="text-neutral-500 text-xs mt-1">What to bring</p>
            </Link>
            <Link to="/retreats/benefits" className="bg-white/70 p-4 rounded-xl border border-gold/10 hover:border-gold/30 transition-all text-center">
              <span className="text-neutral-800 font-medium text-sm">Retreat Benefits</span>
              <p className="text-neutral-500 text-xs mt-1">Why go on retreat</p>
            </Link>
            <Link to="/retreats/beginners" className="bg-white/70 p-4 rounded-xl border border-gold/10 hover:border-gold/30 transition-all text-center">
              <span className="text-neutral-800 font-medium text-sm">Beginner's Guide</span>
              <p className="text-neutral-500 text-xs mt-1">New to yoga?</p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WhatToExpectYogaRetreat;
